import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FaChartBar, FaEnvelope, FaTrash, FaFilter, FaBuilding, 
  FaGraduationCap, FaCalendarAlt, FaCertificate, FaUsers,
  FaArrowLeft, FaSync, FaDownload
} from 'react-icons/fa';
import { toast } from 'sonner';
import { adminApi, getToken, getAdminUser } from '@/lib/adminAuth';

const campus = [
  "Abidjan Cocody-Angré",
  "Abengourou",
  "Bondoukou",
  "Bonoua",
  "Accra (Ghana)"
];

export default function AdminStatistics() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState('');
  
  // States for statistics
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // States for contact messages
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    campus: '',
    filiere: ''
  });
  
  // Active tab
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    setIsAuthenticated(true);
    setCurrentUser(getAdminUser() || '');
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStatistics();
      fetchMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchStatistics = async () => {
    setLoadingStats(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.campus) params.append('campus', filters.campus);
      if (filters.filiere) params.append('filiere', filters.filiere);
      
      const response = await adminApi.get(`/admin/statistics?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await adminApi.get('/admin/contact-messages');
      setMessages(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;
    
    try {
      await adminApi.delete(`/admin/contact-messages/${messageId}`);
      toast.success('Message supprimé');
      fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchStatistics();
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      campus: '',
      filiere: ''
    });
    setTimeout(() => fetchStatistics(), 100);
  };

  const exportStats = () => {
    if (!stats) return;
    
    const data = {
      total_preinscriptions: stats.total_preinscriptions,
      total_contacts: stats.total_contacts,
      par_campus: stats.by_campus,
      par_filiere: stats.by_filiere,
      par_diplome: stats.by_diploma,
      par_date: stats.by_date
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `statistiques_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Button
                onClick={() => navigate('/admin/preinscriptions')}
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-brand-primary"
              >
                <FaArrowLeft className="mr-2" />
                Retour
              </Button>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-brand-secondary">
              Statistiques & Messages
            </h1>
            <p className="text-gray-600">Connecté en tant que : <strong>{currentUser}</strong></p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={exportStats}
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
            >
              <FaDownload className="mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'stats'
                ? 'bg-brand-primary text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FaChartBar className="inline mr-2" />
            Statistiques
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'messages'
                ? 'bg-brand-primary text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FaEnvelope className="inline mr-2" />
            Messages ({messages.length})
          </button>
        </div>

        {/* Statistics Tab */}
        {activeTab === 'stats' && (
          <>
            {/* Filters */}
            <Card className="p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <FaFilter className="text-brand-primary" />
                <h2 className="text-lg font-bold text-brand-secondary">Filtres</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Date début</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date fin</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Campus</Label>
                  <Select value={filters.campus || "all"} onValueChange={(value) => handleFilterChange('campus', value === "all" ? '' : value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Tous les campus" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les campus</SelectItem>
                      {campus.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2 lg:col-span-2">
                  <Button onClick={applyFilters} className="bg-brand-primary hover:bg-brand-primary-dark text-white">
                    <FaFilter className="mr-2" />
                    Appliquer
                  </Button>
                  <Button onClick={resetFilters} variant="outline">
                    <FaSync className="mr-2" />
                    Réinitialiser
                  </Button>
                </div>
              </div>
            </Card>

            {loadingStats ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des statistiques...</p>
              </div>
            ) : stats && (
              <>
                {/* Summary Cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <Card className="p-6 bg-gradient-to-br from-brand-primary to-brand-primary-dark text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm">Préinscriptions</p>
                        <p className="text-3xl font-bold">{stats.total_preinscriptions}</p>
                      </div>
                      <FaUsers className="text-4xl text-white/30" />
                    </div>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-brand-secondary to-blue-900 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm">Messages Contact</p>
                        <p className="text-3xl font-bold">{stats.total_contacts}</p>
                      </div>
                      <FaEnvelope className="text-4xl text-white/30" />
                    </div>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-green-600 to-green-700 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm">Campus actifs</p>
                        <p className="text-3xl font-bold">{stats.by_campus?.length || 0}</p>
                      </div>
                      <FaBuilding className="text-4xl text-white/30" />
                    </div>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-purple-600 to-purple-700 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm">Filières demandées</p>
                        <p className="text-3xl font-bold">{stats.by_filiere?.length || 0}</p>
                      </div>
                      <FaGraduationCap className="text-4xl text-white/30" />
                    </div>
                  </Card>
                </div>

                {/* Detailed Stats */}
                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                  {/* By Campus */}
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-brand-secondary mb-4 flex items-center gap-2">
                      <FaBuilding className="text-brand-primary" />
                      Par Campus
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {stats.by_campus?.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">{item._id || 'Non spécifié'}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-brand-primary h-2 rounded-full" 
                                style={{ width: `${(item.count / stats.total_preinscriptions) * 100}%` }}
                              ></div>
                            </div>
                            <span className="bg-brand-primary text-white px-3 py-1 rounded-full text-sm font-bold min-w-[40px] text-center">
                              {item.count}
                            </span>
                          </div>
                        </div>
                      ))}
                      {(!stats.by_campus || stats.by_campus.length === 0) && (
                        <p className="text-gray-500 text-center py-4">Aucune donnée</p>
                      )}
                    </div>
                  </Card>

                  {/* By Filière */}
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-brand-secondary mb-4 flex items-center gap-2">
                      <FaGraduationCap className="text-brand-primary" />
                      Par Filière
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {stats.by_filiere?.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" title={item._id}>
                            {item._id || 'Non spécifié'}
                          </span>
                          <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {item.count}
                          </span>
                        </div>
                      ))}
                      {(!stats.by_filiere || stats.by_filiere.length === 0) && (
                        <p className="text-gray-500 text-center py-4">Aucune donnée</p>
                      )}
                    </div>
                  </Card>

                  {/* By Diploma */}
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-brand-secondary mb-4 flex items-center gap-2">
                      <FaCertificate className="text-brand-primary" />
                      Par Niveau Sollicité
                    </h3>
                    <div className="space-y-3">
                      {stats.by_diploma?.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">{item._id || 'Non spécifié'}</span>
                          <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {item.count}
                          </span>
                        </div>
                      ))}
                      {(!stats.by_diploma || stats.by_diploma.length === 0) && (
                        <p className="text-gray-500 text-center py-4">Aucune donnée</p>
                      )}
                    </div>
                  </Card>

                  {/* By Type Formation */}
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-brand-secondary mb-4 flex items-center gap-2">
                      <FaGraduationCap className="text-brand-primary" />
                      Par Type de Formation
                    </h3>
                    <div className="space-y-3">
                      {stats.by_type_formation?.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 uppercase">{item._id || 'Non spécifié'}</span>
                          <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {item.count}
                          </span>
                        </div>
                      ))}
                      {(!stats.by_type_formation || stats.by_type_formation.length === 0) && (
                        <p className="text-gray-500 text-center py-4">Aucune donnée</p>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Timeline / By Date */}
                <Card className="p-6 mb-8">
                  <h3 className="text-lg font-bold text-brand-secondary mb-4 flex items-center gap-2">
                    <FaCalendarAlt className="text-brand-primary" />
                    Évolution des Inscriptions (30 derniers jours)
                  </h3>
                  <div className="overflow-x-auto">
                    <div className="flex gap-2 min-w-max py-4">
                      {stats.by_date?.slice().reverse().map((item, index) => (
                        <div key={index} className="flex flex-col items-center">
                          <div 
                            className="w-8 bg-brand-primary rounded-t"
                            style={{ height: `${Math.max(item.count * 20, 10)}px` }}
                          ></div>
                          <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">
                            {item._id?.slice(5) || ''}
                          </span>
                          <span className="text-xs font-bold text-brand-primary mt-4">
                            {item.count}
                          </span>
                        </div>
                      ))}
                      {(!stats.by_date || stats.by_date.length === 0) && (
                        <p className="text-gray-500 text-center py-4 w-full">Aucune donnée temporelle</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Recent Registrations */}
                <Card className="p-6">
                  <h3 className="text-lg font-bold text-brand-secondary mb-4">
                    Dernières Préinscriptions
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left p-3">Matricule</th>
                          <th className="text-left p-3">Nom</th>
                          <th className="text-left p-3">Campus</th>
                          <th className="text-left p-3">Filière</th>
                          <th className="text-left p-3">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_registrations?.map((reg, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3 font-mono font-medium text-brand-primary">{reg.matricule || 'N/A'}</td>
                            <td className="p-3">{reg.nom} {reg.prenom}</td>
                            <td className="p-3">{reg.campus || '-'}</td>
                            <td className="p-3 truncate max-w-[200px]" title={reg.filiere}>{reg.filiere}</td>
                            <td className="p-3 text-gray-500">
                              {new Date(reg.created_at).toLocaleDateString('fr-FR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
          </>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-brand-secondary flex items-center gap-2">
                <FaEnvelope className="text-brand-primary" />
                Messages de Contact ({messages.length})
              </h2>
              <Button onClick={fetchMessages} variant="outline" size="sm">
                <FaSync className="mr-2" />
                Actualiser
              </Button>
            </div>

            {loadingMessages ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des messages...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div key={msg.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-brand-secondary">{msg.sujet}</h4>
                        <p className="text-sm text-gray-500">
                          De: <span className="font-medium">{msg.nom}</span> ({msg.email})
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {new Date(msg.created_at).toLocaleString('fr-FR')}
                        </span>
                        <Button
                          onClick={() => deleteMessage(msg.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                      {msg.message}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <a
                        href={`mailto:${msg.email}?subject=Re: ${msg.sujet}`}
                        className="text-sm text-brand-primary hover:underline flex items-center gap-1"
                      >
                        <FaEnvelope />
                        Répondre par email
                      </a>
                    </div>
                  </div>
                ))}

                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <FaEnvelope className="text-5xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun message de contact</p>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
